import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import toast from 'react-hot-toast'

interface OrderStatus {
  orderNumber: string
  status: string
  statusHistory: Array<{
    status: string
    message?: string
    updatedAt: string
    updatedBy?: string
  }>
  items: Array<{
    title: string
    isExpress: boolean
    quantity: number
    device?: {
      brand: string
      model: string
    }
    repair?: {
      title: string
    }
  }>
  totalAmount: number
  trackingNumber?: string
  createdAt: string
  updatedAt: string
}

const statusDefinitions: {[key: string]: string} = {
  'pending': 'Bestellung eingegangen',
  'confirmed': 'Bestellung bestätigt',
  'payment_received': 'Zahlung erhalten',
  'device_shipped_to_us': 'Gerät zu uns unterwegs',
  'device_received': 'Gerät bei uns eingetroffen',
  'diagnosis_in_progress': 'Diagnose läuft',
  'repair_in_progress': 'Reparatur läuft',
  'repair_completed': 'Reparatur abgeschlossen',
  'device_shipped_back': 'Gerät zurück versendet',
  'completed': 'Auftrag abgeschlossen',
  'cancelled': 'Auftrag storniert'
}

const OrderTrackingPage: React.FC = () => {
  const { orderId } = useParams()
  const [orderNumber, setOrderNumber] = useState('')
  const [orderStatus, setOrderStatus] = useState<OrderStatus | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'

  // Auto-fill order number if provided via URL
  useEffect(() => {
    if (orderId) {
      setOrderNumber(orderId)
      // Auto-track if order ID is provided in URL
      handleTrackOrder(null, orderId)
    }
  }, [orderId])

  const handleTrackOrder = async (e: React.FormEvent | null, orderIdParam?: string) => {
    if (e) e.preventDefault()
    setIsLoading(true)
    setError('')
    setOrderStatus(null)
    
    const trackingNumber = orderIdParam || orderNumber
    
    if (!trackingNumber.trim()) {
      setError('Bitte geben Sie eine Bestellnummer ein')
      setIsLoading(false)
      return
    }

    try {
      const response = await fetch(`${apiUrl}/tracking/${trackingNumber}`)
      const data = await response.json()
      
      if (data.success) {
        setOrderStatus(data.data)
      } else {
        setError(data.message || 'Bestellung nicht gefunden')
        toast.error(data.message || 'Bestellung nicht gefunden')
      }
    } catch (error) {
      console.error('Error tracking order:', error)
      setError('Fehler beim Laden der Bestelldaten')
      toast.error('Fehler beim Laden der Bestelldaten')
    } finally {
      setIsLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    const statusColorMap: {[key: string]: string} = {
      'pending': 'text-gray-600',
      'confirmed': 'text-blue-600',
      'payment_received': 'text-green-600',
      'device_shipped_to_us': 'text-yellow-600',
      'device_received': 'text-indigo-600',
      'diagnosis_in_progress': 'text-indigo-600',
      'repair_in_progress': 'text-orange-600',
      'repair_completed': 'text-emerald-600',
      'device_shipped_back': 'text-cyan-600',
      'completed': 'text-green-600',
      'cancelled': 'text-indigo-600'
    }
    return statusColorMap[status] || 'text-gray-600'
  }


  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold text-center mb-8">Sendungsverfolgung</h1>
          
          <div className="bg-white p-6 rounded-lg shadow mb-8">
            <form onSubmit={handleTrackOrder} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bestellnummer
                </label>
                <input
                  type="text"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  placeholder="z.B. ORD-1758013955920"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                />
              </div>
              
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                {isLoading ? 'Suche...' : 'Status abfragen'}
              </button>
            </form>

            {error && (
              <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                <p className="text-indigo-600">{error}</p>
              </div>
            )}
          </div>
          
          {orderStatus && (
            <div className="space-y-8">
              {/* Order Overview */}
              <div className="bg-white p-6 rounded-lg shadow">
                <h2 className="text-xl font-semibold mb-4">Bestellübersicht</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-gray-600">Bestellnummer</p>
                    <p className="font-semibold text-lg">{orderStatus.orderNumber}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-600">Aktueller Status</p>
                    <p className={`font-semibold text-lg ${getStatusColor(orderStatus.status)}`}>
                      {statusDefinitions[orderStatus.status] || orderStatus.status}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-600">Bestelldatum</p>
                    <p className="font-semibold">{new Date(orderStatus.createdAt).toLocaleDateString('de-DE')}</p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-600">Gesamtbetrag</p>
                    <p className="font-semibold">{orderStatus.totalAmount.toFixed(2)}€</p>
                  </div>

                  {orderStatus.trackingNumber && (
                    <div className="md:col-span-2">
                      <p className="text-sm text-gray-600">DHL Sendungsnummer</p>
                      <div className="flex items-center space-x-2">
                        <p className="font-semibold">{orderStatus.trackingNumber}</p>
                        <a
                          href={`https://www.dhl.de/de/privatkunden/pakete-empfangen/verfolgen.html?piececode=${orderStatus.trackingNumber}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                        >
                          Bei DHL verfolgen →
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Ordered Items */}
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">Bestellte Reparaturen</h3>
                <div className="space-y-3">
                  {orderStatus.items.map((item, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                      <div>
                        <p className="font-medium">{item.title}</p>
                        {item.device && (
                          <p className="text-sm text-gray-600">{item.device.brand} {item.device.model}</p>
                        )}
                        {item.isExpress && (
                          <span className="inline-block bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded mt-1">
                            Express Service
                          </span>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{item.quantity}x</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Status Timeline */}
              {orderStatus.statusHistory && orderStatus.statusHistory.length > 0 && (
                <div className="bg-white p-6 rounded-lg shadow">
                  <h3 className="text-lg font-semibold mb-4">Status Verlauf</h3>
                  <div className="space-y-4">
                    {orderStatus.statusHistory.map((entry, index) => (
                      <div key={index} className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-4 h-4 bg-indigo-600 rounded-full mt-1"></div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="font-medium">{entry.status}</p>
                            <p className="text-sm text-gray-500">
                              {new Date(entry.updatedAt).toLocaleString('de-DE')}
                            </p>
                          </div>
                          {entry.message && (
                            <p className="text-sm text-gray-600 mt-1">{entry.message}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact Info */}
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-2">Fragen zu Ihrer Reparatur?</h3>
                <p className="text-gray-700 mb-4">
                  Unser Support-Team hilft Ihnen gerne weiter!
                </p>
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                  <a
                    href="tel:01605010907"
                    className="bg-indigo-600 text-white px-4 py-2 rounded font-medium hover:bg-indigo-700 text-center"
                  >
                    📞 0160-5010907
                  </a>
                  <a
                    href="mailto:service@phone-medic.de"
                    className="bg-white text-indigo-600 border border-indigo-600 px-4 py-2 rounded font-medium hover:bg-indigo-50 text-center"
                  >
                    ✉️ service@phone-medic.de
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default OrderTrackingPage